// Mock data for the frontend-only phase. Values mirror the Asia Wallet Figma
// so the built screens match the design 1:1. Replaced by live API data in phase 3.
import type { Card, Coin, Transaction, UserProfile, Wallet } from '@asia/shared';

export const mockCoins: Coin[] = [
  {
    symbol: 'USDT',
    name: 'Tether',
    chain: 'TRON',
    balance: 1_000_001,
    priceUsd: 1.0,
    change24h: 0,
  },
  {
    symbol: 'TRX',
    name: 'Tron',
    chain: 'TRON',
    balance: 10_001,
    priceUsd: 0.32,
    change24h: 0.021,
  },
];

export const mockWallets: Wallet[] = [
  {
    id: 'w1',
    name: 'Main wallet',
    address: 'TJRabc1234WolfXyz5678KpQr9012MnBv34',
    coins: mockCoins,
    createdAt: '2025-06-01T10:00:00Z',
  },
  {
    id: 'w2',
    name: 'Savings',
    address: 'TKsav9876Zzz5432Aaaa1111Bbbb2222Cccc',
    coins: [{ ...mockCoins[0]!, balance: 25_000 }, { ...mockCoins[1]!, balance: 300 }],
    createdAt: '2025-06-10T12:00:00Z',
  },
];

export const mockTransactions: Transaction[] = [
  {
    id: 't1',
    type: 'receive',
    status: 'confirmed',
    coin: 'USDT',
    chain: 'TRON',
    amount: 200,
    amountUsd: 199.78,
    date: '2025-06-23T20:12:00Z',
    address: 'TJRabc1234WolfXyz5678KpQr9012MnBv34',
    explorerUrl: 'https://tronscan.org/#/transaction/6a054f50147c150b91db7654064f809b9d543729d61d1e0af2fa645646172f58',
  },
  {
    id: 't2',
    type: 'qr',
    status: 'confirmed',
    coin: 'USDT',
    chain: 'TRON',
    amount: -24,
    amountUsd: 23.97,
    feeUsd: 0.5,
    date: '2025-06-23T13:02:00Z',
    merchant: '7-Eleven',
    mcc: '5411',
    paymentType: 'PromptPay',
    storeAmount: 850,
    storeCurrency: 'THB',
    explorerUrl: 'https://tronscan.org/#/transaction/d48561fe63f09940de401f1082e4714d6145c6af434c287ff08d62c90b40b357',
  },
  {
    id: 't3',
    type: 'send',
    status: 'confirmed',
    coin: 'USDT',
    chain: 'TRON',
    amount: -540,
    amountUsd: 539.4,
    feeUsd: 1.2,
    date: '2025-06-23T20:12:00Z',
    address: 'TWsend000Recipient1111Address2222Xx',
    explorerUrl: 'https://tronscan.org/#/transaction/c30db8585a2509649b253b1b673810939461736d10d961d97ac1aa27dbe99bee',
  },
  {
    id: 't4',
    type: 'topup',
    status: 'confirmed',
    coin: 'USDT',
    chain: 'TRON',
    amount: 200,
    amountUsd: 199.78,
    feeUsd: 1,
    date: '2025-06-21T09:41:00Z',
    explorerUrl: 'https://tronscan.org/#/transaction/7f7eb741bad79cae5353f7ab1de47c24bb7a8dfef2f5657c8c97727642fde5da',
  },
  {
    id: 't5',
    type: 'buy',
    status: 'confirmed',
    coin: 'USDT',
    chain: 'TRON',
    amount: 100,
    amountUsd: 98.0,
    feeUsd: 1,
    date: '2025-06-21T08:15:00Z',
    explorerUrl: 'https://tronscan.org/#/transaction/9289cd6306a194748fcdc8583f9d874ebabfcbee819fe1e99c0ea6f52ff6ba82',
  },
];

export const mockCards: Card[] = [
  {
    id: 'c1',
    label: 'Asia Wallet Light',
    brand: 'visa',
    last4: '8720',
    balanceUsd: 1727.87,
    status: 'active',
    supportsApplePay: true,
    supportsGooglePay: true,
    theme: 'light',
    expMonth: 8,
    expYear: 28,
    number: '4000 0012 3456 8720',
    cvv: '123',
  },
];

export const mockProfile: UserProfile = {
  telegramId: 100200300,
  nickname: 'wolf.t.me',
  email: '',
  pinSet: false,
  biometricEnabled: false,
};

// Demo 24-word seed for the View Seed screen (mock phase only).
export const mockSeed: string[] = [
  'legal', 'winner', 'thank', 'year', 'wave', 'sausage',
  'worth', 'useful', 'legal', 'winner', 'thank', 'yellow',
  'abandon', 'amount', 'liar', 'quote', 'shell', 'burden',
  'sausage', 'nature', 'trigger', 'method', 'salute', 'render',
];

export const mockSupport = {
  telegram: 'https://t.me/asiawallet_support',
  email: 'support@asia.wallet',
};

export interface FaqItem {
  q: { en: string; ru: string };
  a: { en: string; ru: string };
}

export const mockFaq: FaqItem[] = [
  {
    q: { en: 'What is Asia Wallet?', ru: 'Что такое Asia Wallet?' },
    a: {
      en: 'Asia Wallet is a non-custodial TRON wallet for USDT and TRX with virtual cards and QR payments. You fully control your funds — only you hold the seed phrase.',
      ru: 'Asia Wallet — некастодиальный TRON-кошелёк для USDT и TRX с виртуальными картами и оплатой по QR. Вы полностью контролируете средства — сид-фраза известна только вам.',
    },
  },
  {
    q: { en: 'How do I top up my wallet?', ru: 'Как пополнить кошелёк?' },
    a: {
      en: 'Open the Receive screen to get your TRON (TRC-20) address and send USDT or TRX to it, or use Buy to purchase crypto with a card.',
      ru: 'Откройте экран «Получить», чтобы увидеть свой адрес TRON (TRC-20), и отправьте на него USDT или TRX, либо используйте «Купить» для покупки картой.',
    },
  },
  {
    q: { en: 'What are the network fees?', ru: 'Какие комиссии сети?' },
    a: {
      en: 'Transfers on the TRON network cost a small fee in TRX (usually around 1 USDT equivalent). The exact fee is shown before you confirm any transaction.',
      ru: 'Переводы в сети TRON стоят небольшую комиссию в TRX (обычно около 1 USDT). Точная комиссия показывается перед подтверждением операции.',
    },
  },
  {
    q: { en: 'Is my wallet secure?', ru: 'Насколько безопасен мой кошелёк?' },
    a: {
      en: 'Your keys never leave your device. Protect access with a passcode and Face ID, and always keep your 24-word seed phrase private and offline.',
      ru: 'Ключи никогда не покидают ваше устройство. Защитите доступ код-паролем и Face ID и всегда храните сид-фразу из 24 слов в тайне и офлайн.',
    },
  },
  {
    q: { en: 'How do virtual cards work?', ru: 'Как работают виртуальные карты?' },
    a: {
      en: 'You can issue a VISA virtual card funded from your balance and use it for online payments, Apple Pay and Google Pay.',
      ru: 'Вы можете выпустить виртуальную карту VISA, пополняемую с баланса, и использовать её для онлайн-оплаты, Apple Pay и Google Pay.',
    },
  },
];

/** Active Telegram sessions (mock). Sessions are valid for 7 days. */
export interface Session {
  id: string;
  device: string;
  ip: string;
  lastActive: string; // ISO
  createdAt: string; // ISO — expires 7 days after this
  current?: boolean;
}

/** "Now" for the mock phase (matches the mock tx dates). */
export const MOCK_NOW = '2025-06-25T00:00:00Z';

export const mockSessions: Session[] = [
  {
    id: 's1',
    device: 'iPhone 15 Pro · Telegram iOS',
    ip: '85.102.44.17',
    lastActive: '2025-06-24T21:05:00Z',
    createdAt: '2025-06-24T09:00:00Z',
    current: true,
  },
  {
    id: 's2',
    device: 'MacBook Pro · Telegram macOS',
    ip: '85.102.44.17',
    lastActive: '2025-06-24T18:40:00Z',
    createdAt: '2025-06-21T14:30:00Z',
  },
  {
    id: 's3',
    device: 'Pixel 8 · Telegram Android',
    ip: '176.33.12.90',
    lastActive: '2025-06-23T11:20:00Z',
    createdAt: '2025-06-19T08:12:00Z',
  },
];

export function totalBalanceUsd(coins: Coin[]): number {
  return coins.reduce((sum, c) => sum + c.balance * c.priceUsd, 0);
}
